import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

const assessmentData = [
    {
        id: 1,
        title: "How can I prepare for the assessment?",
        desc: "The best way to prepare is to take some time to understand the role and refresh the skills most relevant to it. Practice problem-solving, review key concepts, and ensure you have a quiet space with a reliable internet connection. Most importantly, be yourself, stay focused, and approach the assessment with confidence and a positive mindset.",
        isBlue: false
    },
    {
        id: 2,
        title: "What happens after the technical assessment?",
        desc: "After you complete the technical assessment, our team takes the time to review your submission and understand your approach to solving problems. If your skills and experience align with the role, we'll reach out to schedule the next steps, which may include interviews with our team. We'll keep you updated throughout the process and ensure clear communication at every stage.",
        isBlue: false
    },
    {
        id: 3,
        title: "Why does Mobikode use technical assessments?",
        desc: "At Mobikode, we believe a resume tells only part of your story. Our technical assessments give you an opportunity to showcase how you think, solve problems, and apply your skills in real-world situations. It helps us understand your strengths better and ensures every candidate gets a fair chance to demonstrate their potential.",
        isBlue: false
    },
    {
        id: 4,
        title: "What should I expect from the assessment?",
        desc: "The assessment is an opportunity for you to showcase your skills, experience, and way of thinking. You can expect a mix of role-related tasks, practical challenges, or questions designed to understand how you approach real-world situations. There’s no trick involved, we simply want to get to know your strengths and potential better.",
        isBlue: false
    }
];

const TechnicalAssessment = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const [sliderRef, instanceRef] = useKeenSlider(
        {
            initial: 0,
            loop: true,
            slideChanged(slider) {
                setCurrentSlide(slider.track.details.rel);
            },
            created() {
                setLoaded(true);
            },
            breakpoints: {
                "(min-width: 640px)": {
                    slides: { perView: 1.5, spacing: 20 },
                },
                "(min-width: 768px)": {
                    slides: { perView: 2.2, spacing: 20 },
                },
                "(min-width: 1024px)": {
                    slides: { perView: 2.4, spacing: 22 },
                },
            },
            slides: { perView: 1.1, spacing: 16 },
        },
        [
            (slider) => {
                let timeout;
                let mouseOver = false;
                function clearNextTimeout() {
                    clearTimeout(timeout);
                }
                function nextTimeout() {
                    clearTimeout(timeout);
                    if (mouseOver) return;
                    timeout = setTimeout(() => {
                        slider.next();
                    }, 3000);
                }
                slider.on("created", () => {
                    slider.container.addEventListener("mouseover", () => {
                        mouseOver = true;
                        clearNextTimeout();
                    });
                    slider.container.addEventListener("mouseout", () => {
                        mouseOver = false;
                        nextTimeout();
                    });
                    nextTimeout();
                });
                slider.on("dragStarted", clearNextTimeout);
                slider.on("animationEnded", nextTimeout);
                slider.on("updated", nextTimeout);
            },
        ]
    );

    const fadeUpVariant = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <section className="w-full py-[40px] md:pb-[100px] overflow-hidden bg-white">
            <div className="max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px]">

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeUpVariant}
                    className="flex justify-start mb-[24px] md:mb-[60px]"
                >
                    <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] tracking-tight text-start">
                        Preparing for the technical assessment
                    </h2>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={fadeUpVariant}
                    className="relative"
                >
                    <div ref={sliderRef} className="keen-slider !overflow-visible lg:!overflow-hidden">
                        {assessmentData.map((slide) => (
                            <div className="keen-slider__slide" key={slide.id}>
                                <div
                                    className="group flex flex-col p-[24px] sm:p-[30px] md:p-[40px] transition-colors duration-500 h-[280px] sm:h-[400px] cursor-pointer bg-[#F0F0F0] md:hover:bg-[#01386A] rounded-sm"
                                >
                                    <h3
                                        className="font-sora text-[16px] md:text-[20px] font-[600] mb-[12px] md:mb-[20px] transition-colors duration-300 text-[#01386A] md:group-hover:text-white"
                                    >
                                        {slide.title}
                                    </h3>

                                    <p
                                        className="font-graphik text-[14px] md:text-[16px] font-[500] leading-[1.5] transition-colors duration-300 text-[#6C7689] md:group-hover:text-white"
                                    >
                                        {slide.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {loaded && instanceRef.current && (
                        <div className="flex items-center gap-[16px] mt-[24px] md:mt-[40px]">
                            <button
                                onClick={(e) => e.stopPropagation() || instanceRef.current?.prev()}
                                className={`w-[40px] h-[40px] flex items-center justify-center rounded-full border border-[#ED6800] text-[#ED6800] transition-colors hover:bg-[#ED6800] hover:text-white`}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                onClick={(e) => e.stopPropagation() || instanceRef.current?.next()}
                                className="w-[40px] h-[40px] flex items-center justify-center rounded-full border border-[#ED6800] text-[#ED6800] hover:bg-[#ED6800] hover:text-white transition-colors"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    )}
                </motion.div>

            </div>
        </section>
    );
};

export default TechnicalAssessment;